import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Select, 
  Switch, 
  Space, 
  Divider, 
  Typography, 
  Collapse,
  Modal,
  message,
  Tooltip,
  Radio,
  Tag
} from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  ArrowUpOutlined, 
  ArrowDownOutlined,
  CopyOutlined,
  ExportOutlined,
  ImportOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { FormConfig, FormFieldConfig } from '../../components/DynamicForm';
import { DynamicForm } from '../../components/DynamicForm';
import { formConfigManager } from '../../utils/formConfigManager';
import { saveFormConfig, getProtocols, ProtocolItem } from '../../services/api';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { TextArea } = Input;
const { Option } = Select;

// 设备类型选项（完整列表）
const DEVICE_TYPES = [
  { label: '塔吊', value: 0 },
  { label: '扬尘', value: 1 },
  { label: '视频', value: 3 },
  { label: '卸料平台', value: 4 },
  { label: '施工升降机', value: 5 },
  { label: '围栏', value: 6 },
  { label: '高支模', value: 7 },
  { label: '考勤机', value: 9 }
];

interface FormConfigEditorProps {
  initialConfig?: FormConfig;
  onSave?: (config: FormConfig) => void;
  formType?: number; // 0: 项目表单，1: 设备表单
  protocolId?: number; // 关联的协议 ID（用于项目表单）
  deviceType?: number; // 设备类型（用于设备表单）
  areaId?: number;     // 区域 ID（用于设备表单）
}

const FIELD_TYPES = [
  { label: '文本输入框', value: 'input' },
  { label: '多行文本', value: 'textarea' },
  { label: '数字输入框', value: 'number' },
  { label: '下拉选择框', value: 'select' },
  { label: '日期选择器', value: 'date' },
  { label: '日期范围选择器', value: 'dateRange' },
  { label: '开关', value: 'switch' },
  { label: '单选框组', value: 'radio' },
  { label: '复选框组', value: 'checkbox' }
];

const FormConfigEditor: React.FC<FormConfigEditorProps> = ({ initialConfig, onSave, formType = 0, protocolId, deviceType, areaId }) => {
  // 使用默认配置或初始配置
  const defaultConfig: FormConfig = {
    id: `form_${Date.now()}`,
    name: '新表单',
    description: '表单描述',
    fields: [],
    type: formType,
    device_type: undefined, // 默认不设置设备类型
    protocolId: undefined,  // 默认不关联协议
    area_id: areaId,        // 区域 ID（用于设备表单）
    templateType: undefined,
    tags: []
  };

  const [config, setConfig] = useState<FormConfig>(initialConfig || defaultConfig);
  const [form] = Form.useForm();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewMode, setPreviewMode] = useState<'create' | 'edit' | 'view'>('create');
  const [jsonVisible, setJsonVisible] = useState(false);
  const [jsonValue, setJsonValue] = useState('');
  const [selectedFormType, setSelectedFormType] = useState<number>(formType);
  const [selectedDeviceType, setSelectedDeviceType] = useState<number | undefined>(
    initialConfig?.device_type ?? deviceType
  );
  const [selectedProtocolId, setSelectedProtocolId] = useState<number | undefined>(
    initialConfig?.protocolId ?? protocolId
  );
  const [selectedAreaId, setSelectedAreaId] = useState<number | undefined>(
    initialConfig?.area_id ?? areaId
  );
  const [customName, setCustomName] = useState<string>(
    initialConfig?.tags && initialConfig.tags.length > 0 ? initialConfig.tags[0] : ''
  );
  
  // 协议列表状态
  const [protocols, setProtocols] = useState<ProtocolItem[]>([]);
  const [protocolLoading, setProtocolLoading] = useState(false);
  const [protocolSearch, setProtocolSearch] = useState('');

  // 当初始配置变化时更新状态
  useEffect(() => {
    if (initialConfig) {
      setConfig(initialConfig);
      // 只有当初始配置中的表单类型为有效值 (0 或 1) 时才更新表单类型状态
      if (initialConfig.type === 0 || initialConfig.type === 1) {
        setSelectedFormType(initialConfig.type);
      }
      // 更新设备类型
      if (typeof initialConfig.device_type === 'number') {
        setSelectedDeviceType(initialConfig.device_type);
      }
      // 更新协议 ID
      if (typeof initialConfig.protocolId === 'number') {
        setSelectedProtocolId(initialConfig.protocolId);
      }
      // 更新区域 ID（设备表单）
      if (typeof initialConfig.area_id === 'number') {
        setSelectedAreaId(initialConfig.area_id);
      }
      // 更新自定义名称（从 tags 中提取）
      if (initialConfig.tags && initialConfig.tags.length > 0) {
        setCustomName(initialConfig.tags[0]);
      }
    }
  }, [initialConfig]);

  // 当 formType 变化时更新状态
  useEffect(() => {
    setSelectedFormType(formType);
  }, [formType]);

  // 获取协议列表
  const loadProtocols = async (search?: string) => {
    try {
      setProtocolLoading(true);
      const response = await getProtocols(search, 100);
      
      if (response.data.code === 0 && response.data.data) {
        setProtocols(response.data.data);
      }
    } catch (error) {
      console.error('Error loading protocols:', error);
      message.warning('获取协议列表失败，将显示默认选项');
    } finally {
      setProtocolLoading(false);
    }
  };

  // 初始加载协议列表
  useEffect(() => {
    loadProtocols();
  }, []);

  // 当外部传入的 protocolId 变化时更新状态
  useEffect(() => {
    if (protocolId !== undefined) {
      setSelectedProtocolId(protocolId);
    }
  }, [protocolId]);

  // 当外部传入的 deviceType 变化时更新状态
  useEffect(() => {
    if (deviceType !== undefined) {
      setSelectedDeviceType(deviceType);
    }
  }, [deviceType]);

  // 当外部传入的 areaId 变化时更新状态
  useEffect(() => {
    if (areaId !== undefined) {
      setSelectedAreaId(areaId);
    }
  }, [areaId]);

  // 生成表单 ID 预览
  const generateFormIdPreview = (): string => {
    if (selectedFormType === 0) {
      // 项目表单：project_${protocolId}_${customName}
      const protocol = selectedProtocolId || 0;
      return customName ? `project_${protocol}_${customName}` : `project_${protocol}`;
    } else {
      // 设备表单：device_{protocol_id}_{device_type}[_{customName}]
      const protocol = selectedProtocolId || 0;
      const devType = selectedDeviceType || 0;
      return customName ? `device_${protocol}_${devType}_${customName}` : `device_${protocol}_${devType}`;
    }
  };

  const generatedFormId = generateFormIdPreview();

  // 更新表单基本信息
  const updateFormInfo = (values: any) => {
    setConfig(prev => ({
      ...prev,
      name: values.name,
      description: values.description,
      // 不在这里更新device_type，避免与handleDeviceTypeChange冲突
    }));
  };

  // 更新表单类型
  const handleFormTypeChange = (type: number) => {
    // 只有当选择的类型为有效值(0或1)时才更新
    if (type === 0 || type === 1) {
      setSelectedFormType(type);
      // 不在这里更新config，避免多次状态更新
    }
  };

  // 更新设备类型
  const handleDeviceTypeChange = (type: number) => {
    setSelectedDeviceType(type);
    // 更新config中的device_type
    setConfig(prev => ({
      ...prev,
      device_type: type
    }));
  };

  // 添加新字段
  const addField = () => {
    const newField = formConfigManager.createField({});
    setConfig(prev => ({
      ...prev,
      fields: [...prev.fields, newField]
    }));
  };

  // 更新字段
  const updateField = (index: number, field: Partial<FormFieldConfig>) => {
    setConfig(prev => {
      const newFields = [...prev.fields];
      newFields[index] = { ...newFields[index], ...field };
      return { ...prev, fields: newFields };
    });
  };

  // 删除字段
  const deleteField = (index: number) => {
    setConfig(prev => {
      const newFields = [...prev.fields];
      newFields.splice(index, 1);
      return { ...prev, fields: newFields };
    });
  };

  // 移动字段
  const moveField = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === config.fields.length - 1)
    ) {
      return;
    }

    setConfig(prev => {
      const newFields = [...prev.fields];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      const temp = newFields[index];
      newFields[index] = newFields[targetIndex];
      newFields[targetIndex] = temp;
      return { ...prev, fields: newFields };
    });
  };

  // 复制字段
  const duplicateField = (index: number) => {
    setConfig(prev => {
      const fieldToDuplicate = { ...prev.fields[index] };
      fieldToDuplicate.key = `${fieldToDuplicate.key}_copy_${Date.now()}`;
      const newFields = [...prev.fields];
      newFields.splice(index + 1, 0, fieldToDuplicate);
      return { ...prev, fields: newFields };
    });
  };

  // 保存配置
  const saveConfig = async () => {
    if (config.fields.length === 0) {
      message.warning('表单至少需要一个字段');
      return;
    }

    // 检查字段键是否唯一
    const keys = config.fields.map(field => field.key);
    const uniqueKeys = new Set(keys);
    if (keys.length !== uniqueKeys.size) {
      message.error('字段键必须唯一');
      return;
    }

    // 生成新的表单 ID
    let newFormId: string;
    if (selectedFormType === 0) {
      // 项目表单：project_${protocolId}_${customName}
      const protocol = selectedProtocolId || 0;
      newFormId = customName ? `project_${protocol}_${customName}` : `project_${protocol}`;
    } else {
      // 设备表单：device_{protocol_id}_{device_type}[_{customName}]
      const protocol = selectedProtocolId || 0;
      const devType = selectedDeviceType || 0;
      newFormId = customName ? `device_${protocol}_${devType}_${customName}` : `device_${protocol}_${devType}`;
    }

    // 创建要保存的配置对象
    const configToSave: FormConfig = { 
      ...config,
      id: newFormId,
      type: selectedFormType === 0 || selectedFormType === 1 ? selectedFormType : 0,
      device_type: selectedDeviceType,
      protocolId: selectedProtocolId,
      area_id: selectedAreaId, // 保存区域 ID
      tags: customName ? [customName] : []
    };
    
    if (onSave) {
      // 调用父组件的保存方法，而不是直接调用 API
      onSave(configToSave);
      message.success('表单配置已提交');
    } else {
      // 如果没有提供 onSave 回调，则直接调用 API
      try {
        const response = await saveFormConfig(configToSave);
        if (response.data.code === 0) {
          message.success('表单配置已保存');
        } else {
          message.error(response.data.msg || '保存失败');
        }
      } catch (error) {
        message.error('保存失败');
        console.error(error);
      }
    }
  };

  // 导出配置为 JSON
  const exportConfig = () => {
    // 创建要导出的配置对象
    const configToExport: FormConfig = { 
      ...config,
      id: generatedFormId,
      type: selectedFormType === 0 || selectedFormType === 1 ? selectedFormType : 0,
      device_type: selectedDeviceType,
      protocolId: selectedProtocolId,
      tags: customName ? [customName] : []
    };
    
    setJsonValue(JSON.stringify(configToExport, null, 2));
    setJsonVisible(true);
  };

  // 导入JSON配置
  const importConfig = () => {
    setJsonValue('');
    setJsonVisible(true);
  };

  // 处理JSON导入
  const handleJsonImport = () => {
    try {
      const importedConfig = JSON.parse(jsonValue);
      
      // 简单验证导入的配置
      if (!importedConfig.id || !importedConfig.name || !Array.isArray(importedConfig.fields)) {
        throw new Error('无效的表单配置格式');
      }
      
      setConfig(importedConfig);
      // 如果导入的配置包含表单类型，则更新表单类型
      if (typeof importedConfig.type === 'number') {
        setSelectedFormType(importedConfig.type);
      }
      // 如果导入的配置包含设备类型，则更新设备类型
      if (typeof importedConfig.device_type === 'number') {
        setSelectedDeviceType(importedConfig.device_type);
      }
      setJsonVisible(false);
      message.success('配置已导入');
    } catch (error) {
      message.error('导入失败: 无效的JSON格式');
    }
  };

  // 渲染字段选项编辑器
  const renderOptionsEditor = (field: FormFieldConfig, index: number) => {
    if (!['select', 'radio', 'checkbox'].includes(field.type)) {
      return null;
    }

    return (
      <Form.Item label="选项">
        <Form.List name={`fields.${index}.options`} initialValue={field.options || []}>
          {(fields, { add, remove }) => (
            <>
              {fields.map((optionField, optionIndex) => (
                <Space key={optionField.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                  <Form.Item
                    {...optionField}
                    name={[optionField.name, 'label']}
                    rules={[{ required: true, message: '请输入选项标签' }]}
                    noStyle
                  >
                    <Input placeholder="选项标签" style={{ width: 120 }} />
                  </Form.Item>
                  <Form.Item
                    {...optionField}
                    name={[optionField.name, 'value']}
                    rules={[{ required: true, message: '请输入选项值' }]}
                    noStyle
                  >
                    <Input placeholder="选项值" style={{ width: 120 }} />
                  </Form.Item>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => remove(optionField.name)}
                  />
                </Space>
              ))}
              <Button
                type="dashed"
                onClick={() => add({ label: '', value: '' })}
                icon={<PlusOutlined />}
                block
              >
                添加选项
              </Button>
            </>
          )}
        </Form.List>
      </Form.Item>
    );
  };

  // 渲染字段编辑器
  const renderFieldEditor = () => {
    return (
      <Collapse defaultActiveKey={['basic']} items={[
        {
          key: 'basic',
          label: '基本信息',
          children: (
            <Form
              layout="vertical"
              initialValues={{
                name: config.name,
                description: config.description,
                formType: selectedFormType,
                deviceType: selectedDeviceType
              }}
              onValuesChange={(_, values) => updateFormInfo(values)}
            >
              <Form.Item
                label="表单名称"
                name="name"
                rules={[{ required: true, message: '请输入表单名称' }]}
              >
                <Input placeholder="请输入表单名称" />
              </Form.Item>
              <Form.Item
                label="表单描述"
                name="description"
              >
                <TextArea placeholder="请输入表单描述" rows={2} />
              </Form.Item>
              <Form.Item label="表单类型">
                <Radio.Group 
                  value={selectedFormType} 
                  onChange={(e) => handleFormTypeChange(e.target.value)}
                  buttonStyle="solid"
                >
                  <Radio.Button value={0}>项目表单</Radio.Button>
                  <Radio.Button value={1}>设备表单</Radio.Button>
                </Radio.Group>
              </Form.Item>
              
              {/* 协议选择器 - 项目表单和设备表单都显示 */}
              <Form.Item 
                label="关联协议" 
                tooltip="选择此表单关联的协议 ID，支持搜索"
              >
                <Select
                  placeholder="请输入关键词搜索协议"
                  value={selectedProtocolId}
                  onChange={setSelectedProtocolId}
                  onSearch={(value) => {
                    setProtocolSearch(value);
                    loadProtocols(value);
                  }}
                  showSearch
                  filterOption={false}
                  loading={protocolLoading}
                  style={{ width: '100%' }}
                  allowClear
                  notFoundContent={protocolLoading ? '加载中...' : '未找到协议'}
                >
                  {protocols.map(protocol => (
                    <Option key={protocol.id} value={protocol.id}>
                      {protocol.title || `协议 #${protocol.id}`}
                      {protocol.projectCount !== undefined && (
                        <Tag style={{ marginLeft: 8 }}>项目：{protocol.projectCount}</Tag>
                      )}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              
              {/* 设备类型选择器 - 仅设备表单显示 */}
              {selectedFormType === 1 && (
                <Form.Item 
                  label="设备类型" 
                  tooltip="选择此表单关联的设备类型"
                >
                  <Select
                    placeholder="请选择设备类型"
                    value={selectedDeviceType}
                    onChange={handleDeviceTypeChange}
                    style={{ width: '100%' }}
                  >
                    {DEVICE_TYPES.map(type => (
                      <Option key={type.value} value={type.value}>
                        {type.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              {/* 自定义名称 - 用于生成表单 ID */}
              <Form.Item 
                label="表单自定义后缀"
                tooltip="用于生成表单 ID，例如：basic, advanced, minimal"
              >
                <Input 
                  placeholder="例如：basic"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  addonBefore="后缀"
                />
              </Form.Item>

              {/* 表单 ID 预览 */}
              <Form.Item label="生成的表单 ID">
                <Input 
                  value={generatedFormId}
                  readOnly
                  addonBefore="ID"
                  style={{ fontFamily: 'monospace' }}
                />
              </Form.Item>
              
            </Form>
          )
        },
        {
          key: 'fields',
          label: '表单字段',
          children: (
            <>
              {config.fields.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                  <Text type="secondary">暂无字段，请添加字段</Text>
                </div>
              ) : (
                <Form
                  form={form}
                  layout="vertical"
                  initialValues={{ fields: config.fields }}
                >
                  {config.fields.map((field, index) => (
                    <Card
                      key={index}
                      size="small"
                      title={`字段 ${index + 1}: ${field.label || '未命名'}`}
                      style={{ marginBottom: 16 }}
                      extra={
                        <Space>
                          <Tooltip title="上移">
                            <Button
                              type="text"
                              icon={<ArrowUpOutlined />}
                              disabled={index === 0}
                              onClick={() => moveField(index, 'up')}
                            />
                          </Tooltip>
                          <Tooltip title="下移">
                            <Button
                              type="text"
                              icon={<ArrowDownOutlined />}
                              disabled={index === config.fields.length - 1}
                              onClick={() => moveField(index, 'down')}
                            />
                          </Tooltip>
                          <Tooltip title="复制">
                            <Button
                              type="text"
                              icon={<CopyOutlined />}
                              onClick={() => duplicateField(index)}
                            />
                          </Tooltip>
                          <Tooltip title="删除">
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => deleteField(index)}
                            />
                          </Tooltip>
                        </Space>
                      }
                    >
                      <Form.Item
                        label="字段标签"
                        name={['fields', index, 'label']}
                        initialValue={field.label}
                        rules={[{ required: true, message: '请输入字段标签' }]}
                      >
                        <Input
                          placeholder="请输入字段标签"
                          onChange={e => updateField(index, { label: e.target.value })}
                        />
                      </Form.Item>
                      
                      <Form.Item
                        label="字段键名"
                        name={['fields', index, 'key']}
                        initialValue={field.key}
                        rules={[{ required: true, message: '请输入字段键名' }]}
                        tooltip="字段键名用于表单数据的存储，必须唯一"
                      >
                        <Input
                          placeholder="请输入字段键名"
                          onChange={e => updateField(index, { key: e.target.value })}
                        />
                      </Form.Item>
                      
                      <Form.Item
                        label="字段类型"
                        name={['fields', index, 'type']}
                        initialValue={field.type}
                      >
                        <Select
                          placeholder="请选择字段类型"
                          onChange={value => updateField(index, { type: value })}
                        >
                          {FIELD_TYPES.map(type => (
                            <Option key={type.value} value={type.value}>
                              {type.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                      
                      <Form.Item
                        label="是否必填"
                        name={['fields', index, 'required']}
                        initialValue={field.required}
                        valuePropName="checked"
                      >
                        <Switch
                          onChange={checked => updateField(index, { required: checked })}
                        />
                      </Form.Item>
                      
                      <Form.Item
                        label="占位文本"
                        name={['fields', index, 'placeholder']}
                        initialValue={field.placeholder}
                      >
                        <Input
                          placeholder="请输入占位文本"
                          onChange={e => updateField(index, { placeholder: e.target.value })}
                        />
                      </Form.Item>
                      
                      {renderOptionsEditor(field, index)}
                    </Card>
                  ))}
                </Form>
              )}
              
              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addField}
                style={{ width: '100%', marginTop: 16 }}
              >
                添加字段
              </Button>
            </>
          )
        }
      ]} />
    );
  };

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={4}>表单配置编辑器</Title>
          <Space>
            <Tooltip title="预览模式">
              <Select
                value={previewMode}
                onChange={(value) => setPreviewMode(value)}
                style={{ width: 120 }}
                size="small"
              >
                <Option value="create">创建模式</Option>
                <Option value="edit">编辑模式</Option>
                <Option value="view">查看模式</Option>
              </Select>
            </Tooltip>
            <Button icon={<EyeOutlined />} onClick={() => setPreviewVisible(true)}>
              预览
            </Button>
            <Button icon={<ImportOutlined />} onClick={importConfig}>
              导入
            </Button>
            <Button icon={<ExportOutlined />} onClick={exportConfig}>
              导出
            </Button>
            <Button type="primary" onClick={saveConfig}>
              保存
            </Button>
          </Space>
        </div>
        
        <Divider />
        
        {renderFieldEditor()}
      </Card>
      
      {/* 预览模态框 */}
      <Modal
        title={`表单预览 - ${previewMode === 'create' ? '创建模式' : previewMode === 'edit' ? '编辑模式' : '查看模式'}`}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width={800}
      >
        {/* 预览模式切换 */}
        <div style={{ marginBottom: 16 }}>
          <Radio.Group 
            value={previewMode} 
            onChange={(e) => setPreviewMode(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="create">创建模式</Radio.Button>
            <Radio.Button value="edit">编辑模式</Radio.Button>
            <Radio.Button value="view">查看模式</Radio.Button>
          </Radio.Group>
        </div>

        {/* 示例数据（编辑模式和查看模式使用） */}
        {previewMode !== 'create' && (
          <div style={{ marginBottom: 16, padding: '12px', background: '#f5f5f5', borderRadius: 8 }}>
            <Text strong>示例数据：</Text>
            <Paragraph style={{ marginTop: 8, fontSize: 12 }}>
              {JSON.stringify({
                name: '示例项目名称',
                projectCode: 'PROJ-001',
                region: 'xinlu',
                appKey: 'example_app_key',
                ...config.fields.reduce((acc, field) => ({
                  ...acc,
                  [field.key]: field.type === 'switch' ? true : '示例值'
                }), {})
              }, null, 2)}
            </Paragraph>
            <Text type="secondary" style={{ fontSize: 12 }}>
              提示：实际数据将由后端返回
            </Text>
          </div>
        )}
        
        <DynamicForm
          config={config}
          mode={previewMode}
          initialValues={previewMode !== 'create' ? {
            name: '示例项目名称',
            projectCode: 'PROJ-001',
            region: 'xinlu',
            appKey: 'example_app_key',
            ...config.fields.reduce((acc, field) => ({
              ...acc,
              [field.key]: field.type === 'switch' ? true : '示例值'
            }), {})
          } : undefined}
          onFinish={values => {
            console.log('预览表单提交:', values);
            message.success('预览提交成功（仅演示）');
          }}
        />
      </Modal>
      
      {/* JSON导入/导出模态框 */}
      <Modal
        title={jsonValue ? "导出配置" : "导入配置"}
        open={jsonVisible}
        onCancel={() => setJsonVisible(false)}
        footer={jsonValue ? [
          <Button key="close" onClick={() => setJsonVisible(false)}>
            关闭
          </Button>
        ] : [
          <Button key="cancel" onClick={() => setJsonVisible(false)}>
            取消
          </Button>,
          <Button key="import" type="primary" onClick={handleJsonImport}>
            导入
          </Button>
        ]}
        width={800}
      >
        {jsonValue ? (
          <div>
            <Paragraph>
              <Text strong>配置JSON:</Text>
            </Paragraph>
            <TextArea
              value={jsonValue}
              rows={20}
              readOnly
            />
          </div>
        ) : (
          <div>
            <Paragraph>
              <Text strong>请粘贴表单配置的JSON:</Text>
            </Paragraph>
            <TextArea
              value={jsonValue}
              onChange={e => setJsonValue(e.target.value)}
              placeholder="粘贴JSON配置..."
              rows={20}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default FormConfigEditor; 